import { notifications } from '@mantine/notifications';
import { useMutation, useSuspenseQueries } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { getLogger } from 'loglevel';

import { ErrorIcon, Success } from '@apple/assets/icons';
import { useTranslation } from '@apple/lib/i18next';
import { useBackOrNavigate } from '@apple/lib/tanstack-router/hooks/use-router-history';
import { DetailsLayout } from '@apple/ui/layouts/DetailsLayout';
import { ServerValidationError } from '@apple/utils/api/errors';

import { requireAuth } from '@apple/features/auth/utils/require-auth';
import { categoryQueries } from '@apple/features/product/queries/category';
import { productQueryKeys, productQueryOptions } from '@apple/features/product/queries/management';
import { saveProduct } from '@apple/features/product-retail/api/management';
import { ProductForm } from '@apple/features/product-retail/components/ProductManagement/ProductForm';
import type { ProductFormData } from '@apple/features/product-retail/models/management';
import { PRODUCTS_ODATA_QUERY_KEY } from '@apple/features/product-retail/queries/odata';
import { propagateChanges } from '@apple/features/store-events/api/change-propagation';
import { saveProductVintages } from '@apple/features/vintage/api';

const log = getLogger('manage-product');

export const Route = createFileRoute('/_authed/_admin/manage/products_/add')({
	beforeLoad: args => {
		requireAuth(args, {
			requirePermission: 'Manzanita.Security.Features.ProductManagement.Write',
		});
		return {
			keywordsQueryOptions: productQueryOptions.keywords,
			productLinesQueryOptions: categoryQueries.all,
		};
	},
	loader: ({ context: { queryClient, keywordsQueryOptions, productLinesQueryOptions } }) => {
		void queryClient.prefetchQuery(keywordsQueryOptions);
		void queryClient.prefetchQuery(productLinesQueryOptions);
	},
	wrapInSuspense: true,
	component: AddProductRoute,
});

function AddProductRoute() {
	const { t } = useTranslation('product');
	const { productLinesQueryOptions, keywordsQueryOptions, queryClient } = Route.useRouteContext();

	const [productCategoriesQuery, keywordsQuery] = useSuspenseQueries({
		queries: [productLinesQueryOptions, keywordsQueryOptions],
	});

	const goBackToList = useBackOrNavigate();

	function showErrorNotification(error: Error) {
		if (!(error instanceof ServerValidationError)) {
			log.error(error);
			notifications.show({
				message: t('common:error.generic'),
				icon: <ErrorIcon />,
				color: 'red',
				autoClose: 5000,
			});
		}
	}

	const addProductMutation = useMutation({
		mutationFn: async (product: ProductFormData) => {
			await saveProduct(product, 'N/A');
			if (product.vintageIds !== undefined && product.vintageIds.length > 0) {
				await saveProductVintages(product.itemId, product.vintageIds);
			}
		},
		onSuccess: async (_, product) => {
			if (product.eventRulePropagations && product.eventRulePropagations.length > 0) {
				await propagateChanges(product.eventRulePropagations, 'N/A');
			}
			void queryClient.invalidateQueries({
				queryKey: productQueryKeys.unassignedSkus,
				exact: true,
			});
			void queryClient.invalidateQueries({
				queryKey: [PRODUCTS_ODATA_QUERY_KEY],
			});

			void queryClient.invalidateQueries({ queryKey: productQueryKeys.keywords });

			notifications.show({
				message: t('notifications.created'),
				icon: <Success />,
				color: 'green',
				autoClose: 5000,
			});
			await goBackToList('/manage/products');
		},
		onError: showErrorNotification,
	});

	return (
		<DetailsLayout title={t('titles.addProduct')}>
			<ProductForm
				productCategoriesQuery={productCategoriesQuery}
				keywordsQuery={keywordsQuery}
				saveProductMutation={addProductMutation}
			/>
		</DetailsLayout>
	);
}
